import { useState } from 'react'
import { apiFetch } from '@/lib/apiFetch'

// Format an epoch-millis instant for a <input type="datetime-local"> value (local tz).
function toLocalInputValue(epochMs: number): string {
  const d = new Date(epochMs)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatDateTime(epochMs: number): string {
  return new Date(epochMs).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

export default function TvPauseAutoOff({ tvId, autoOffOverrideUntil, onUpdate }: {
  tvId: number
  autoOffOverrideUntil: number | null
  onUpdate: () => void
}) {
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)
  const [until, setUntil] = useState(() => toLocalInputValue(Date.now() + 2 * 60 * 60 * 1000))
  const overrideActive = autoOffOverrideUntil != null && autoOffOverrideUntil > Date.now()

  const save = async () => {
    const untilMs = new Date(until).getTime()
    if (!untilMs || untilMs <= Date.now()) return
    setSaving(true)
    try {
      await apiFetch(`/tvs/${tvId}/override`, { method: 'PUT', body: JSON.stringify({ until: untilMs }) })
      setEditing(false)
      onUpdate()
    } finally {
      setSaving(false)
    }
  }

  const clear = async () => {
    setSaving(true)
    try {
      await apiFetch(`/tvs/${tvId}/override`, { method: 'DELETE' })
      onUpdate()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium">Pause Auto-Off</p>
          <p className="text-[11px] text-muted-foreground">
            Keep this TV on until a set time, ignoring the schedule
          </p>
        </div>
        {!overrideActive && !editing && (
          <button
            onClick={() => { setUntil(toLocalInputValue(Date.now() + 2 * 60 * 60 * 1000)); setEditing(true) }}
            className="text-xs text-primary font-medium hover:underline shrink-0"
          >
            Set
          </button>
        )}
      </div>

      {overrideActive ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
            Auto-off paused until {formatDateTime(autoOffOverrideUntil!)}
          </span>
          <button onClick={clear} disabled={saving}
            className="text-xs text-amber-700 dark:text-amber-400 font-semibold hover:underline shrink-0">
            Resume
          </button>
        </div>
      ) : editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="datetime-local"
            value={until}
            min={toLocalInputValue(Date.now())}
            onChange={e => setUntil(e.target.value)}
            className="px-2 py-1 border border-border rounded-lg bg-background text-xs"
          />
          <button onClick={save} disabled={saving}
            className="text-xs text-primary font-medium hover:underline">Save</button>
          <button onClick={() => setEditing(false)}
            className="text-xs text-muted-foreground hover:underline">Cancel</button>
        </div>
      ) : null}
    </div>
  )
}
